import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { logger } from '@/services/logger';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

interface UseNavigationDropdownValidationUpdaterProps {
    hierarchyItemIds: string[];
}

/**
 * Subscribes to VALIDATION_FINISHED_EVENT and updates the findByPrimaryKeys React Query cache
 * when validation finishes for any item in the hierarchy. This keeps hasWarnings/isCompliant
 * in the navigation dropdown in sync with the latest validation state.
 */
export function useNavigationDropdownValidationUpdater({ hierarchyItemIds }: UseNavigationDropdownValidationUpdaterProps) {
    const queryClient = useQueryClient();
    const hierarchyItemIdsRef = useRef<string[]>([]);

    const { subscribe, unsubscribe } = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.VALIDATION_FINISHED_EVENT,
        identifier: undefined,
        eventHandler: async (data: any) => {
            if (!data?.validationResults) return;

            const validatedItemIds = Object.keys(data.validationResults);
            const hierarchyValidatedIds = hierarchyItemIdsRef.current.filter((id: string) =>
                validatedItemIds.includes(id.toString())
            );

            if (hierarchyValidatedIds.length === 0) return;

            logger.debug(
                'NavigationDropdown validation updater: Processing validation updates for hierarchy items:',
                hierarchyValidatedIds
            );

            try {
                for (const id of hierarchyValidatedIds) {
                    const result = data.validationResults[id];
                    if (!result) continue;

                    const allQueries = queryClient.getQueriesData({ queryKey: ['findByPrimaryKeys'] });

                    allQueries.forEach(([key, oldData]: [QueryKey, unknown]) => {
                        if (!oldData || !Array.isArray(oldData)) return;

                        const newData = (oldData as any[]).map((item: any) =>
                            item?.primaryKey__ === id
                                ? {
                                    ...item,
                                    validation__: result.validationErrors,
                                    validation_warnings__: result.validationWarnings
                                }
                                : item
                        );

                        queryClient.setQueryData(key, newData);
                    });
                }
            } catch (error) {
                logger.error('Failed to update navigation dropdown item validation:', error);
            }
        }
    });

    hierarchyItemIdsRef.current = hierarchyItemIds;

    useEffect(() => {
        if (hierarchyItemIds.length === 0) return;

        subscribe(true);

    }, [hierarchyItemIds.length, subscribe, unsubscribe]);
}
